<template>
    <v-container class="mt-10 pb-12" style="min-width: 1000px; max-width: 1400px; padding-left: 24px; padding-right: 24px;">
        <div class="d-flex align-center justify-space-between mb-6">
            <div>
                <h1 class="text-h4 font-weight-bold mb-1">Needs review</h1>
                <p class="text-subtitle-1 grey--text mb-0">
                    Sessions the system flagged. Flagged time still counts until you reject it.
                </p>
            </div>
            <div class="d-flex align-center" style="gap: 8px;">
                <v-btn outlined class="text-none" :to="{ name: 'TimeTracking' }">
                    <v-icon left>mdi-arrow-left</v-icon>Time Tracking
                </v-btn>
                <v-btn color="primary" outlined :loading="loading" class="text-none" @click="load">
                    <v-icon left>mdi-refresh</v-icon>Refresh
                </v-btn>
            </div>
        </div>

        <v-card outlined>
            <v-tabs v-model="status" @change="load">
                <v-tab href="#pending" class="text-none">
                    Pending
                    <v-chip v-if="status === 'pending' && sessions.length" x-small color="warning" class="ml-2">
                        {{ sessions.length }}
                    </v-chip>
                </v-tab>
                <v-tab href="#rejected" class="text-none">Rejected</v-tab>
                <v-tab href="#approved" class="text-none">Approved</v-tab>
            </v-tabs>
            <v-divider />

            <div v-if="loading" class="pa-6 text-center">
                <v-progress-circular indeterminate size="28" />
            </div>
            <div v-else-if="!sessions.length" class="pa-6 text-center grey--text">
                {{ emptyText }}
            </div>
            <v-list v-else three-line class="py-0">
                <template v-for="(s, i) in sessions">
                    <v-divider v-if="i" :key="`d${s.id}`" />
                    <v-list-item :key="s.id">
                        <v-list-item-content>
                            <v-list-item-title>
                                <span class="font-weight-medium">{{ s.user_name }}</span>
                                <span class="grey--text"> · {{ formatDate(s.started_at) }} ·
                                    {{ formatDuration(s.duration_seconds) }}</span>
                                <span v-if="s.task_name" class="grey--text"> · {{ s.task_name }}</span>
                            </v-list-item-title>
                            <v-list-item-subtitle class="mt-1">
                                <v-tooltip v-for="(detail, code) in s.flags" :key="code" top max-width="360">
                                    <template v-slot:activator="{ on }">
                                        <v-chip v-on="on" x-small outlined color="warning" class="mr-1 mb-1">
                                            {{ flagLabel(code) }}
                                        </v-chip>
                                    </template>
                                    <span>{{ detail }}</span>
                                </v-tooltip>
                            </v-list-item-subtitle>
                            <v-list-item-subtitle v-if="status !== 'pending'" class="mt-1">
                                {{ status === 'rejected' ? 'Rejected' : 'Approved' }} by
                                {{ s.reviewed_by_name || 'a manager' }}<span v-if="s.review_note"> — {{ s.review_note }}</span>
                            </v-list-item-subtitle>
                        </v-list-item-content>

                        <v-list-item-action v-if="status === 'pending'" class="flex-row align-center" style="gap: 8px;">
                            <v-btn small outlined color="success" class="text-none"
                                :loading="busyId === s.id" :disabled="busyId !== null"
                                @click="decide(s, 'approved')">
                                <v-icon left small>mdi-check</v-icon>Approve
                            </v-btn>
                            <v-btn small outlined color="error" class="text-none"
                                :disabled="busyId !== null" @click="startReject(s)">
                                <v-icon left small>mdi-close</v-icon>Reject
                            </v-btn>
                        </v-list-item-action>
                        <v-list-item-action v-else-if="status === 'rejected'" class="flex-row align-center">
                            <v-btn small outlined color="success" class="text-none"
                                :loading="busyId === s.id" :disabled="busyId !== null"
                                @click="decide(s, 'approved')">
                                <v-icon left small>mdi-undo</v-icon>Restore time
                            </v-btn>
                        </v-list-item-action>
                        <v-list-item-action v-else class="flex-row align-center">
                            <v-btn small outlined color="error" class="text-none"
                                :disabled="busyId !== null" @click="startReject(s)">
                                <v-icon left small>mdi-close</v-icon>Reject
                            </v-btn>
                        </v-list-item-action>
                    </v-list-item>
                </template>
            </v-list>
        </v-card>

        <v-dialog v-model="rejectDialog" max-width="460">
            <v-card v-if="rejecting">
                <v-card-title class="text-h6">Reject this time?</v-card-title>
                <v-card-text>
                    <p class="mb-3">
                        {{ formatDuration(rejecting.duration_seconds) }} for {{ rejecting.user_name }} will be
                        removed from totals. They'll see it with your note. You can undo this later.
                    </p>
                    <v-textarea v-model="rejectNote" label="Reason (shown to the employee)" outlined dense
                        rows="2" counter="500" maxlength="500" hide-details="auto" />
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text class="text-none" @click="rejectDialog = false">Cancel</v-btn>
                    <v-btn color="error" class="text-none" :loading="busyId !== null" @click="confirmReject">
                        Reject time
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
const FLAG_LABELS = {
    rewrite_attempt: 'Tried to change ended session',
    overlap: 'Overlapping sessions',
    future_start: 'Starts in the future',
    backdated: 'Backdated',
    clock_skew: 'Clock mismatch',
    implausible_input: 'Implausible input',
    foreign_task: 'Task not theirs',
    excess_hours: 'Very long day',
    duplicate_screenshot: 'Duplicate screenshot',
    near_duplicate_screenshot: 'Similar to another day',
    future_screenshot: 'Screenshot in future',
    backdated_screenshot: 'Old screenshot',
    missing_screenshots: 'No screenshots',
};

export default {
    name: 'SessionReviewsComponent',

    data() {
        return {
            sessions: [],
            loading: false,
            status: 'pending',
            busyId: null,
            rejectDialog: false,
            rejecting: null,
            rejectNote: '',
        };
    },

    computed: {
        emptyText() {
            return {
                pending: 'Nothing waiting for review.',
                rejected: 'No rejected sessions.',
                approved: 'No approved sessions.',
            }[this.status];
        },
    },

    mounted() {
        this.load();
    },

    methods: {
        async load() {
            this.loading = true;
            try {
                const res = await this.$axios.get('api/agent/session-reviews', { status: this.status });
                this.sessions = res.data?.response ? res.data.sessions : [];
            } catch (e) {
                this.sessions = [];
                this.$toast.error(e?.data?.message || 'Failed to load sessions');
            } finally {
                this.loading = false;
            }
        },

        flagLabel(code) {
            return FLAG_LABELS[code] || code;
        },

        formatDate(value) {
            return this.$date(value).format('D MMM YYYY HH:mm');
        },

        formatDuration(seconds) {
            const h = Math.floor(seconds / 3600);
            const m = Math.floor((seconds % 3600) / 60);
            return h ? `${h}h ${m}m` : `${m}m`;
        },

        startReject(session) {
            this.rejecting = session;
            this.rejectNote = '';
            this.rejectDialog = true;
        },

        confirmReject() {
            return this.decide(this.rejecting, 'rejected', this.rejectNote);
        },

        async decide(session, decision, note = null) {
            this.busyId = session.id;
            try {
                const res = await this.$axios.post(`api/agent/session-reviews/${session.id}`, {
                    decision,
                    note: note || null,
                });
                if (res.data?.response) {
                    this.sessions = this.sessions.filter((s) => s.id !== session.id);
                    this.rejectDialog = false;
                    this.$toast.success(
                        decision === 'rejected' ? 'Time rejected'
                            : this.status === 'rejected' ? 'Time restored' : 'Session approved'
                    );
                } else {
                    this.$toast.error(res.data?.message || 'Failed to save decision');
                }
            } catch (e) {
                this.$toast.error(e?.data?.message || 'Failed to save decision');
            } finally {
                this.busyId = null;
            }
        },
    },
};
</script>
